import type { Moment } from 'moment'
import { useCallback, useMemo, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import FlightLookupTile from './FlightLookupTile'
import {
  flightConfirmationCodeConfig,
  formOptions,
} from '../../Common/Fields/config'
import Input from '../../Common/Fields/RHFInput'
import CreateFlight from '../../Create/CreateFlight'

import {
  addToTripClickEvent,
  addToTripSaveErrorEvent,
  addToTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
  DMY_CASUAL_FORMAT,
} from 'constants/dateFormats'
import { toggleHas } from 'lib/array/setUtils'
import { useAirports } from 'tripPlanner/hooks/api/flights'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import { useGetFlightSchedules } from 'tripPlanner/hooks/api/misc'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { FlightSchedule } from 'tripPlanner/types/common'
import type { CreateFlightRequestBody } from 'tripPlanner/utils/itemConversions/common'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'

interface Props {
  flightCode: string
  departureDate: Moment
  tripId: string
}

type FormValues = { confirmationCode?: string }

const EMPTY_FLIGHT_ARRAY: Array<FlightSchedule> = []

export default function FlightLookupListing({
  flightCode,
  departureDate,
  tripId,
}: Props) {
  const intl = useIntl()
  const [selectedKeys, setSelectedKeys] = useState(new Set<string>())
  const toggleKey = useCallback(
    (id: string) =>
      setSelectedKeys((selectedKeys) => {
        const newSelections = new Set([...selectedKeys])
        toggleHas(newSelections, id)
        return newSelections
      }),
    [],
  )
  const { closeModal, goBack } = useModalCallbacks()
  const openModal = useOpenModal()

  const methods = useForm<FormValues>({
    ...formOptions,
    defaultValues: {},
  })
  const { getValues, handleSubmit, register } = methods

  const { mutate: createTripItems, isLoading: isCreatingFlights } =
    useCreateTripItemBatch({
      onSuccess: (result) => {
        fireInteractionEvent(addToTripSaveEvent('flights_custom_lookup'))
        scrollToNewItemWithRetry(result.updatedItemIds[0])
        closeModal()
      },
      onError: () => {
        fireInteractionEvent(addToTripSaveErrorEvent('flights_custom_lookup'))
      },
    })

  const {
    data: flights,
    isLoading,
    isError,
  } = useGetFlightSchedules(
    { departureDate, flightCode },
    {
      onSuccess: (flights) => {
        setSelectedKeys(new Set(flights.map((flight) => flight.key)))
      },
    },
  )

  const selectedFlights = useMemo(
    () =>
      (flights ?? EMPTY_FLIGHT_ARRAY).filter((flight) =>
        selectedKeys.has(flight.key),
      ),
    [flights, selectedKeys],
  )
  const hasSelectedFlight = selectedFlights.length > 0

  const airportCodes = useMemo(() => {
    if (!flights) {
      return []
    }
    const codes = flights.flatMap((f) => [
      f.departureAirportCode,
      f.arrivalAirportCode,
    ])
    return [...new Set(codes)].sort()
  }, [flights])

  const airportResults = useAirports({ codes: airportCodes })

  const airportLoaded = (airportCode: string) =>
    airportResults.some((r) => r.data?.airportCode === airportCode)

  const hasAllRequiredAirportData = selectedFlights.every(
    (flight) =>
      airportLoaded(flight.departureAirportCode) &&
      airportLoaded(flight.arrivalAirportCode),
  )

  const airportsLoading =
    !hasAllRequiredAirportData && airportResults.some((r) => r.isLoading)

  const saveFlight: SubmitHandler<FormValues> = useCallback(
    (values) => {
      // This will only fire if there is a selectedFlight
      // Yes there's probably better ways of doing it, crunch time!
      if (selectedFlights.length > 0 && hasAllRequiredAirportData) {
        const tripItems: Array<CreateFlightRequestBody> = selectedFlights.map(
          (flight) => {
            // We know these will exist here due the the `hasAllRequiredAirportData` check
            const departureAirport = airportResults.find(
              (r) => r.data?.airportCode === flight.departureAirportCode,
            )!.data!
            const arrivalAirport = airportResults.find(
              (r) => r.data?.airportCode === flight.arrivalAirportCode,
            )!.data!

            return {
              type: 'FLIGHT',
              airlineName: flight.airlineName,
              flightNumber: flightCode,
              destinationAirportCode: flight.arrivalAirportCode,
              originAirportCode: flight.departureAirportCode,
              sourceType: 'NA',
              startDate: flight.departureDate.format(ISO_DATE_FORMAT),
              startTime: flight.departureTime.format(SHORT_TIME_FORMAT_24_HOUR),
              endDate: flight.arrivalDate.format(ISO_DATE_FORMAT),
              endTime: flight.arrivalTime.format(SHORT_TIME_FORMAT_24_HOUR),
              startPlace: {
                name: departureAirport.airportName || '',
                point: {
                  lat: departureAirport.lt!,
                  lng: departureAirport.lg!,
                },
                type: 'GEO',
              },
              endPlace: {
                name: arrivalAirport.airportName || '',
                point: {
                  lat: arrivalAirport.lt!,
                  lng: arrivalAirport.lg!,
                },
                type: 'GEO',
              },
              confirmationCode: values.confirmationCode,
              isBooked: true,
            }
          },
        )

        createTripItems({ tripId, tripItems })
      }
    },
    [
      selectedFlights,
      hasAllRequiredAirportData,
      createTripItems,
      tripId,
      airportResults,
      flightCode,
    ],
  )

  const createCustomFlight = useCallback(() => {
    fireInteractionEvent(
      addToTripClickEvent('modal_flights_custom_manual', 'postlookup'),
    )
    if (!flights) {
      openModal(
        CreateFlight,
        {
          tripId,
          initialData: {
            flightNumber: flightCode,
            confirmationCode: getValues('confirmationCode'),
            startDate: departureDate.format(ISO_DATE_FORMAT),
            startPlace: {
              name: '',
              type: 'CUSTOM' as const,
            },
          },
        },
        {},
      )
      return
    } else if (selectedFlights.length !== 1 || !hasAllRequiredAirportData) {
      openModal(
        CreateFlight,
        {
          tripId,
          initialData: {
            flightNumber: flightCode,
            airlineName: flights ? flights?.[0]?.airlineName : '',
            confirmationCode: getValues('confirmationCode'),
            startDate: departureDate.format(ISO_DATE_FORMAT),
            startPlace: {
              name: '',
              type: 'CUSTOM' as const,
            },
          },
        },
        {},
      )
      return
    } else {
      const flight = selectedFlights[0]
      // We know these will exist here due the the `hasAllRequiredAirportData` check
      const departureAirport = airportResults.find(
        (r) => r.data?.airportCode === flight.departureAirportCode,
      )!.data!
      const arrivalAirport = airportResults.find(
        (r) => r.data?.airportCode === flight.arrivalAirportCode,
      )!.data!

      openModal(
        CreateFlight,
        {
          tripId,
          initialData: {
            originAirportCode: flight.departureAirportCode,
            destinationAirportCode: flight.arrivalAirportCode,
            startDate: flight.departureDate.format(ISO_DATE_FORMAT),
            startTime: flight.departureTime.format(SHORT_TIME_FORMAT_24_HOUR),
            endDate: flight.arrivalDate.format(ISO_DATE_FORMAT),
            endTime: flight.arrivalTime.format(SHORT_TIME_FORMAT_24_HOUR),
            startPlace: {
              name: departureAirport.airportName || '',
              point: {
                lat: departureAirport.lt!,
                lng: departureAirport.lg!,
              },
              type: 'GEO' as const,
            },
            endPlace: {
              name: arrivalAirport?.airportName || '',
              point: {
                lat: arrivalAirport.lt!,
                lng: arrivalAirport.lg!,
              },
              type: 'GEO' as const,
            },
            flightNumber: flight.airlineCode + flight.flightNumber?.toString(),
            airlineName: flight.airlineName,
            isEndDateSame:
              flight.arrivalDate.toISOString() ===
              flight.departureDate.toISOString(),
            confirmationCode: getValues('confirmationCode'),
          },
        },
        {},
      )
    }
  }, [
    flights,
    selectedFlights,
    hasAllRequiredAirportData,
    openModal,
    tripId,
    flightCode,
    getValues,
    departureDate,
    airportResults,
  ])

  const primaryDisabled =
    airportsLoading || isCreatingFlights || !hasSelectedFlight

  const flightTitle = intl.formatMessage({
    id: 'rrpTvzJzfYo0ljKth5ev',
    defaultMessage: 'Flight',
    description: 'Default modal title for flight lookup in trip planner',
  })

  const findingFlightTitle = intl.formatMessage({
    id: 'MWOAz_6k-Ykrry3pEpJv',
    defaultMessage: 'Finding flight...',
    description: 'Modal title while searching for flight details in the trip planner item editor modal',
  })

  const flightNotFoundTitle = intl.formatMessage({
    id: 'pmB0YEMCMXZrz_kCjDZr',
    defaultMessage: 'Flight not found',
    description: 'Modal title when flight lookup returns no results in the trip planner item editor modal',
  })

  const flightDetailsTitle = intl.formatMessage({
    id: 'QxN5eUhmSZMgYDk6S6KX',
    defaultMessage: 'Flight details',
    description: 'Modal title when flight details are found in the flights flow',
  })

  const confirmationCodeLabel = intl.formatMessage({
    id: '250C5RC_kRC5VNjXYNoD',
    defaultMessage: 'Confirmation code',
    description: 'Label and placeholder for flight confirmation code input field',
  })

  const saveLabel = intl.formatMessage({
    id: 'TncbkyheAYn7a_z8GD5Q',
    defaultMessage: 'Save',
    description: 'Button text to save selected flight to trip in the flights flow',
  })

  const enterManuallyLabel = intl.formatMessage({
    id: 'JKUj8ey5dc6GgOce9ayG',
    defaultMessage: 'or enter details manually',
    description: 'Button text to manually enter flight details instead of using lookup',
  })

  const noFlightsFoundMessage = intl.formatMessage({
    id: 'l9w11wQno57aAKhcBYoc',
    defaultMessage: 'Unfortunately we couldn’t find any flights matching your details.',
    description: 'Message shown when flight lookup finds no matching flights',
  })

  const tryAnotherSearchLabel = intl.formatMessage({
    id: 'nDN0wRo4ggGPMw-gptPo',
    defaultMessage: 'Try another search',
    description: 'Button text to go back and try another flight search',
  })

  const multipleFlightsQuestion = intl.formatMessage({
    id: 'TsziZylYiOL2urvE3CaD',
    defaultMessage: 'Are these your flights?',
    description: 'Question shown when multiple flight results are found',
  })

  const singleFlightQuestion = intl.formatMessage({
    id: '38YyyhQQNiTtVpOoxEYz',
    defaultMessage: 'Is this your flight?',
    description: 'Question shown when a single flight result is found',
  })

  let modalHeaderTitle: string = flightTitle
  if (isLoading) {
    modalHeaderTitle = findingFlightTitle
  } else if (isError) {
    modalHeaderTitle = flightNotFoundTitle
  } else if (flights.length > 0) {
    modalHeaderTitle = flightDetailsTitle
  }

  return (
    <>
      <ModalHeader
        title={modalHeaderTitle}
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            <BodyText variant="large">
              <FormattedMessage
                id="gQZcGuXSXakfVva2s1qH"
                defaultMessage="<bold>{flightCode}</bold> departing on <bold>{departureDate}</bold>"
                description="Flight code and departure date display in flight lookup"
                values={{
                  flightCode,
                  departureDate: departureDate.format(DMY_CASUAL_FORMAT),
                  bold: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>

            {flights && flights.length > 0 && (
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(saveFlight)}>
                  <VerticalSpacer gap={16}>
                    <BodyText variant="large">
                      {flights.length > 1 && multipleFlightsQuestion}
                      {flights.length === 1 && singleFlightQuestion}
                    </BodyText>
                    {flights.map((flight) => (
                      <FlightLookupTile
                        key={flight.key}
                        flight={flight}
                        showSelect={flights.length > 1}
                        selected={selectedKeys.has(flight.key)}
                        onClick={toggleKey}
                      />
                    ))}
                    <Group
                      direction="horizontal"
                      horizontalAlign="stretch"
                      verticalAlign="center"
                      gap={16}
                    >
                      {/* Low priority feature, will follow up with this next sprint (26/10/2022) <Controller
                      name="cabinClass"
                      control={methods.control}
                      render={({ field }) => {
                        return (
                          <Select {...field} label="Type" required>
                            {SELECT_OPTIONS.map((opt) => (
                              <option
                                key={opt.value}
                                value={opt.value}
                                label={opt.label}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                        )
                      }}
                    /> */}
                      <Input
                        {...register('confirmationCode', {
                          ...flightConfirmationCodeConfig,
                        })}
                        label={confirmationCodeLabel}
                        placeholder={confirmationCodeLabel}
                      />
                    </Group>
                    <TextButton
                      kind="primary"
                      size="large"
                      fit="full-width"
                      type="submit"
                      disabled={primaryDisabled}
                    >
                      {saveLabel}
                    </TextButton>
                    <TextButton
                      kind="tertiary"
                      size="large"
                      fit="full-width"
                      onClick={createCustomFlight}
                    >
                      {enterManuallyLabel}
                    </TextButton>
                  </VerticalSpacer>
                </form>
              </FormProvider>
            )}
            {isLoading && (
              <TextLoadingBox typography="body-large" lines={2} width="100%" />
            )}
            {!isLoading && flights?.length === 0 && (
              <>
                <BodyText variant="large">
                  {noFlightsFoundMessage}
                </BodyText>
                <VerticalSpacer gap={16}>
                  <TextButton
                    kind="primary"
                    size="large"
                    fit="full-width"
                    onClick={goBack}
                  >
                    {tryAnotherSearchLabel}
                  </TextButton>
                  <TextButton
                    kind="tertiary"
                    size="large"
                    fit="full-width"
                    onClick={createCustomFlight}
                  >
                    {enterManuallyLabel}
                  </TextButton>
                </VerticalSpacer>
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}
